import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home',      path: '/' },
  { label: 'Services',  path: '/services' },
  { label: 'About',     path: '/about' },
  { label: 'Locations', path: '/locations' },
];

const Navbar: React.FC = () => {
  const [scrollY, setScrollY]   = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef               = useRef<HTMLElement>(null);
  const location                = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY);
      // Also directly mutate the style for butter-smooth feel with no React re-render lag
      if (headerRef.current) {
        const opacity = Math.min(0.06 + (window.scrollY / 90) * 0.90, 0.96);
        const blur    = Math.min((window.scrollY / 90) * 14, 14);
        headerRef.current.style.backgroundColor = `rgba(13, 12, 11, ${opacity})`;
        headerRef.current.style.backdropFilter  = `blur(${blur}px)`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isScrolled = scrollY > 80;

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 w-full z-50"
      style={{
        backgroundColor: 'rgba(13, 12, 11, 0.06)',
        backdropFilter: 'blur(0px)',
        borderBottom: isScrolled ? '1px solid rgba(196, 168, 79, 0.14)' : '1px solid transparent',
        boxShadow: isScrolled ? '0 2px 40px rgba(0,0,0,0.35)' : 'none',
        transition: 'border-bottom 0.5s ease, box-shadow 0.5s ease',
      }}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-[72px]">

        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0" aria-label="Masters Fencing Home">
          <div
            className="px-2 py-1 rounded transition-all duration-700"
            style={{ background: isScrolled ? 'transparent' : 'rgba(13,12,11,0.30)', backdropFilter: isScrolled ? 'none' : 'blur(6px)' }}
          >
            <img
              src="https://lirp.cdn-website.com/9a1ab3f9/dms3rep/multi/opt/remove.photos-removed-background+%2811%29-1920w.png"
              alt="Masters Fencing"
              className="h-10 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.6))' }}
            />
          </div>
        </Link>

        {/* Desktop nav links */}
        <ul className="hidden md:flex items-center gap-8 lg:gap-10">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`relative text-[11px] font-sans font-medium tracking-[0.22em] uppercase transition-colors duration-300 pb-1 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-[#C4A84F]'
                    : 'text-[#F5F1E8]/80 hover:text-[#F5F1E8]'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-[#C4A84F]" />
                )}
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop right side */}
        <div className="hidden md:flex items-center gap-5">
          <a
            href="tel:912-409-2031"
            className="flex items-center gap-2 text-[#F5F1E8]/60 text-[11px] font-sans hover:text-[#C4A84F] transition-colors duration-300 tracking-wide whitespace-nowrap"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="fa-solid fa-phone text-[#C4A84F] text-[10px]" />
            </span>
            912-409-2031
          </a>
          <div className="h-5 w-[1px] bg-[#F5F1E8]/15" />
          <a
            href="#estimate"
            className="bg-[#C4A84F] text-[#0D0C0B] text-[11px] font-bold tracking-[0.18em] uppercase px-5 py-2.5 hover:bg-[#D4BC78] active:scale-[0.97] transition-all duration-300 whitespace-nowrap"
          >
            Request a Consultation
          </a>
        </div>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-[#F5F1E8] cursor-pointer"
          aria-label="Toggle navigation menu"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className={`fa-solid ${menuOpen ? 'fa-xmark' : 'fa-bars'} text-base`} />
          </span>
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ease-out ${
          menuOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#0D0C0B] border-t border-[#C4A84F]/15">
          <ul className="py-5">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className="block px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase text-[#F5F1E8]/65 hover:text-[#C4A84F] hover:bg-white/5 transition-colors font-sans"
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li><div className="mx-6 my-4 h-[1px] bg-[#C4A84F]/15" /></li>
            <li className="px-6 pb-3">
              <a href="tel:912-409-2031" className="flex items-center gap-3 text-[#C4A84F] text-sm font-sans">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="fa-solid fa-phone text-xs" />
                </span>
                912-409-2031
              </a>
            </li>
            <li className="px-6 pb-5">
              <a
                href="#estimate"
                className="block text-center bg-[#C4A84F] text-[#0D0C0B] text-[11px] font-bold tracking-[0.18em] uppercase px-6 py-3.5 hover:bg-[#D4BC78] transition-all whitespace-nowrap"
              >
                Request a Consultation
              </a>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
